import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  ViewButton,
} from '@/components/ui';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import { humanizeStatus } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * Recruitment across the platform, read-only.
 *
 * There are no buttons on this screen and that is the design, not an unfinished
 * page. Whose hiring decision an application is does not change because
 * somebody at Join-Wonder is looking at it, and an admin who could move
 * candidates would be making hiring decisions on a customer's behalf.
 *
 * What an admin needs is to see a company sitting on candidates or running a
 * pipeline it should not be — which is a read. Acting on what they find means
 * moderating the listing or the account, and both are elsewhere with their own
 * audit trail.
 */

const STATUSES = [
  'SUBMITTED',
  'UNDER_REVIEW',
  'SHORTLISTED',
  'INTERVIEW',
  'DECISION_PENDING',
  'OFFERED',
  'OFFER_ACCEPTED',
  'HIRED',
  'REJECTED',
  'WITHDRAWN',
];

// A week without a move is not yet a problem; a month usually is. The number is
// a prompt to look, never a verdict -- some roles genuinely take this long.
const STALE_DAYS = 30;

const PAGE_SIZE = 20;

export function AdminApplicationsPage() {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(status ? { statuses: status } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-applications', params],
    queryFn: () => adminApi.listApplications(params),
    placeholderData: keepPreviousData,
  });

  const activeCount = [search !== '', status !== ''].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setStatus('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Applications"
        description="Every application on the platform. Read-only — moving a candidate is the company's decision, so acting on what you find here means moderating the listing or the account."
      />

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarEnd={
          <>
            <Select
              label="Stage"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Every stage</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {APPLICATION_LABELS[value] ?? humanizeStatus(value)}
                </option>
              ))}
            </Select>
            <SearchInput
              label="Search applications"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Internship title or company name"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading applications…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="No applications"
                description="Nothing matches these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No applications yet"
                description="Applications appear here once students start applying."
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => {
            const stale = row.days_in_stage >= STALE_DAYS;
            return (
              <BoxRow
                key={row.id}
                accent={APPLICATION_TONES[row.status] ?? 'neutral'}
                leading={<BoxAvatar name={row.student_name} round />}
                title={row.student_name}
                to={paths.admin.applicationDetail(row.id)}
                badges={
                  <Badge dot tone={APPLICATION_TONES[row.status] ?? 'neutral'}>
                    {APPLICATION_LABELS[row.status] ?? humanizeStatus(row.status)}
                  </Badge>
                }
                subtitle={row.student_email}
                facts={[
                  {
                    icon: 'internship',
                    text: (
                      <Link
                        to={paths.admin.internshipDetail(row.internship_id)}
                        className="hover:text-brand-700 hover:underline"
                      >
                        {row.internship_title}
                      </Link>
                    ),
                  },
                  {
                    icon: 'building',
                    text: (
                      <Link
                        to={paths.admin.companyDetail(row.company_id)}
                        className="hover:text-brand-700 hover:underline"
                      >
                        {row.company_name}
                      </Link>
                    ),
                  },
                  {
                    icon: 'clockOutline',
                    // A long wait is a prompt to look, so it is the one chip in colour.
                    tone: stale ? 'warning' : undefined,
                    text: `${row.days_in_stage} ${row.days_in_stage === 1 ? 'day' : 'days'} in stage`,
                  },
                  { icon: 'calendar', text: `Applied ${formatDate(row.created_at)}` },
                ]}
                actions={
                  <ViewButton
                    to={paths.admin.applicationDetail(row.id)}
                    title={`Open ${row.student_name}'s application`}
                    label="View application"
                  />
                }
              />
            );
          })}
        </BoxList>
      </ListCard>
    </div>
  );
}
